import { Clip } from '../types/clip-types';
import {
  ScreenshareBound,
  SpeakersAnalysis,
  VisionApiScreenshareResponse
} from '../types/speakers-analysis';
import { getClipBroadcastId } from '../utils/clipUtils';
import { roundToNDecimalPlaces } from '../utils/mathUtils';
import { timeStringToSeconds } from '../utils/timeUtils';
import { findAdjustedBoxBoundaries } from '../utils/speakersAnalysisUtils';
import { getRequiredEnvValue } from '../env';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';

export const defaultSpeakersAnalysis: SpeakersAnalysis = {
  face_ids: [],
  time_analysis: [],
  times: [],
  visible_face_ids: [],
  speaker_mapping: {},
  loading: true,
  adjustedBoxBoundaries: [],
  active_speaker_mapping: {}
};

export interface BaseSpeakerAnalysisOptions {
  clipId: string;
  clip?: Clip | null;
  onAsdComplete?: (clipId: string) => void;
  isLayoutEnabledForPreRecordedEvents: boolean;
  token: string;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  isScreenshareDetectionEnabled: boolean;
}

interface PerClipSpeakersAnalysisOptions extends BaseSpeakerAnalysisOptions {
  start: number;
}

interface FullVideoSpeakersAnalysisOptions extends BaseSpeakerAnalysisOptions {
  contentId: string;
}

export type SpeakersAnalysisOptions = PerClipSpeakersAnalysisOptions | FullVideoSpeakersAnalysisOptions;

const sliceFullVideoAnalysisForClip = (data: SpeakersAnalysis, clip: Clip): SpeakersAnalysis => {
  const start = clip.asset_metadata?.start ?? 0;
  const end = clip.asset_metadata?.end ?? Number.POSITIVE_INFINITY;
  const inRange = (t: number) => t >= start && t <= end;

  const time_analysis = (data.time_analysis ?? []).filter(t => inRange(timeStringToSeconds(t.time)));
  const times = (data.times ?? []).filter(inRange);
  const screenshare_bounds = data.screenshare_bounds?.filter(b => b.start_time < end && b.end_time > start);

  const faceIdsInRange = new Set<number>();
  time_analysis.forEach(t => t.face_positions.forEach(fp => faceIdsInRange.add(fp.face_id)));
  const face_ids = (data.face_ids ?? []).filter(id => faceIdsInRange.has(id));

  return {
    ...data,
    time_analysis,
    times,
    face_ids,
    visible_face_ids: face_ids.slice(0, 4),
    adjustedBoxBoundaries: findAdjustedBoxBoundaries(time_analysis),
    screenshare_bounds
  };
};

const reuseFullFacialRecDataForClip = ({ options }: { options: SpeakersAnalysisOptions }): boolean => {
  if ('contentId' in options) {
    const existingAnalysis =
      options.contentMetadataStoreInstance.getState().data[options.contentId]?.metadata.speakerAnalysis;
    const hasData = (existingAnalysis?.times?.length ?? 0) > 0;
    if (hasData) {
      if (options.clip) {
        const clipScopedAnalysis = sliceFullVideoAnalysisForClip(existingAnalysis as SpeakersAnalysis, options.clip);
        options.contentMetadataStoreInstance.getState().updateSpeakerAnalysis({
          contentId: options.contentId,
          speakerAnalysis: clipScopedAnalysis,
          skipStartTimeOffset: true,
          areLayoutsEnabledForPreRecordedEvents: options.isLayoutEnabledForPreRecordedEvents
        });
      }
      return true;
    }
  }
  return false;
};

export const getSpeakersAnalysis = async (options: SpeakersAnalysisOptions): Promise<void> => {
  if (reuseFullFacialRecDataForClip({ options })) {
    return;
  }
  const { clipId, contentMetadataStoreInstance } = options;

  const url =
    'contentId' in options
      ? `${getRequiredEnvValue('VISION_URL')}/analysis?content_id=${options.contentId}`
      : `${getRequiredEnvValue('VISION_URL')}/analysis?clip_id=${clipId}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${options.token}`
    },
    mode: 'cors'
  });

  if (response.status !== 200) {
    throw new Error('Error fetching speakers analysis');
  }

  const data: Pick<
    SpeakersAnalysis,
    | 'accurate'
    | 'time_analysis'
    | 'face_ids'
    | 'speaker_mapping'
    | 'active_speaker_mapping'
    | 'face_recognition_status'
    | 'face_rec_backend'
  > & {
    screenshares?: VisionApiScreenshareResponse[];
  } = await response.json();

  const {
    accurate,
    face_ids = [],
    time_analysis = [],
    speaker_mapping = {},
    active_speaker_mapping = {},
    face_recognition_status,
    face_rec_backend,
    screenshares
  } = data;

  const timeOffset = 'contentId' in options ? 0 : options.start;
  const updatedTimes = time_analysis.map(t => roundToNDecimalPlaces(timeStringToSeconds(t.time) + timeOffset, 4));

  const adjustedBoxBoundaries = findAdjustedBoxBoundaries(time_analysis);

  const shouldProcessScreenshare = options.isScreenshareDetectionEnabled;
  let screenshare_bounds: ScreenshareBound[] | undefined;

  if (shouldProcessScreenshare && screenshares && screenshares.length > 0) {
    screenshare_bounds = screenshares.map(screenshare => ({
      start_time: roundToNDecimalPlaces(screenshare.start_time + timeOffset, 4),
      end_time: roundToNDecimalPlaces(screenshare.end_time + timeOffset, 4),
      coordinates: {
        x: screenshare.coordinates.x,
        y: screenshare.coordinates.y,
        width: screenshare.coordinates.width,
        height: screenshare.coordinates.height
      }
    }));
  }

  const isFaceRecDone = time_analysis.length > 0;

  const speakerAnalysisData: SpeakersAnalysis = {
    accurate,
    face_ids,
    time_analysis,
    times: updatedTimes,
    visible_face_ids: face_ids.slice(0, 4),
    speaker_mapping,
    loading: !isFaceRecDone,
    adjustedBoxBoundaries,
    active_speaker_mapping,
    screenshare_bounds,
    face_recognition_status,
    face_rec_backend
  };

  if ('contentId' in options) {
    contentMetadataStoreInstance.getState().updateSpeakerAnalysis({
      contentId: options.contentId,
      speakerAnalysis: speakerAnalysisData,
      skipStartTimeOffset: true,
      areLayoutsEnabledForPreRecordedEvents: options.isLayoutEnabledForPreRecordedEvents
    });
    if (options.clip) {
      const clipScopedAnalysis = sliceFullVideoAnalysisForClip(speakerAnalysisData, options.clip);
      contentMetadataStoreInstance.getState().updateSpeakerAnalysis({
        contentId: options.contentId,
        speakerAnalysis: clipScopedAnalysis,
        skipStartTimeOffset: true,
        areLayoutsEnabledForPreRecordedEvents: options.isLayoutEnabledForPreRecordedEvents
      });
    }
  } else {
    const broadcastId = options.clip ? getClipBroadcastId(options.clip) : null;
    if (broadcastId) {
      contentMetadataStoreInstance.getState().updateSpeakerAnalysis({
        contentId: broadcastId,
        speakerAnalysis: speakerAnalysisData,
        areLayoutsEnabledForPreRecordedEvents: options.isLayoutEnabledForPreRecordedEvents
      });
    }
  }

  if (accurate === true && Object.keys(active_speaker_mapping).length > 0) {
    options.onAsdComplete?.(clipId);
  }
};
